import React from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import {
  type CorePlatformFeatureKey,
  hasCorePlatformAccess,
} from "@/utils/accessControl";
import { getDefaultAuthorizedRoute } from "@/utils/defaultAuthorizedRoute";

interface CoreFeatureGuardProps {
  feature: CorePlatformFeatureKey;
  children: React.ReactNode;
  fallbackPath?: string;
}

export const CoreFeatureGuard: React.FC<CoreFeatureGuardProps> = ({
  feature,
  children,
  fallbackPath,
}) => {
  const { t } = useTranslation();
  const { isLoading, userProfile } = useAuthenticatedUser();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  if (!hasCorePlatformAccess(userProfile, feature)) {
    return (
      <Navigate
        to={fallbackPath ?? getDefaultAuthorizedRoute(userProfile)}
        replace
      />
    );
  }

  return <>{children}</>;
};
